import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import ChatWidget from './components/ChatWidget';
import CookieConsent from './components/CookieConsent';
import CouponWidget from './components/CouponWidget';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Challenges from './pages/Challenges';
import Dashboard from './pages/Dashboard';
import ChallengeDetail from './pages/ChallengeDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';
import AdminDashboard from './pages/AdminDashboard';
import AdminCRM from './pages/AdminCRM';
import KYCVerification from './pages/KYCVerification';
import EconomicCalendar from './pages/EconomicCalendar';
import TechnicalAnalysis from './pages/TechnicalAnalysis';
import AdminDocs from './pages/AdminDocs';
import TradingPlatform from './pages/TradingPlatform';
import Withdrawals from './pages/Withdrawals';
import PayoutHistory from './pages/PayoutHistory';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AdminChats from './pages/AdminChats';
import NotificationSettings from './pages/NotificationSettings';
import AdminCoupons from './pages/AdminCoupons';
import Settings from './pages/Settings';
import TradingHistory from './pages/TradingHistory';
import Certificates from './pages/Certificates';
import AffiliatePortal from './pages/AffiliatePortal';
import AffiliateLogin from './pages/AffiliateLogin';
import AdminPackages from './pages/AdminPackages';
import AdminAffiliates from './pages/AdminAffiliates';
import AdminSettings from './pages/AdminSettings';
import AdminRevenue from './pages/AdminRevenue';
import CryptoLaunch from './pages/CryptoLaunch';
import RealFundedAccounts from './pages/RealFundedAccounts';
import GetFunded from './pages/GetFunded';
import KBTCoinPortal from './pages/KBTCoinPortal';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
};

function AppRoutes() {
  return (
    <>
      <Navbar />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/affiliate-login" element={<AffiliateLogin />} />
        <Route path="/challenges" element={<Navigate to="/get-funded" replace />} />
        <Route path="/pricing" element={<Navigate to="/get-funded" replace />} />
        <Route path="/get-funded" element={<GetFunded />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/calendar" element={<EconomicCalendar />} />
        <Route path="/analysis" element={<TechnicalAnalysis />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/crypto" element={<CryptoLaunch />} />
        <Route path="/elite-funded" element={<RealFundedAccounts />} />
        <Route path="/kbtcoin" element={<ProtectedRoute><KBTCoinPortal /></ProtectedRoute>} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/challenge/:id" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
        <Route path="/trade/:challengeId" element={<ProtectedRoute><TradingPlatform /></ProtectedRoute>} />
        <Route path="/verification" element={<ProtectedRoute><KYCVerification /></ProtectedRoute>} />
        <Route path="/kyc" element={<ProtectedRoute><KYCVerification /></ProtectedRoute>} />
        <Route path="/withdrawals/:challengeId" element={<ProtectedRoute><Withdrawals /></ProtectedRoute>} />
        <Route path="/withdrawals" element={<ProtectedRoute><PayoutHistory /></ProtectedRoute>} />
        <Route path="/payouts" element={<ProtectedRoute><PayoutHistory /></ProtectedRoute>} />
        <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/stats" element={<ProtectedRoute><TradingHistory /></ProtectedRoute>} />
        <Route path="/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
        <Route path="/affiliates" element={<ProtectedRoute><AffiliatePortal /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/crm" element={<ProtectedRoute adminOnly><AdminCRM /></ProtectedRoute>} />
        <Route path="/admin/docs" element={<ProtectedRoute adminOnly><AdminDocs /></ProtectedRoute>} />
        <Route path="/admin/chats" element={<ProtectedRoute adminOnly><AdminChats /></ProtectedRoute>} />
        <Route path="/admin/coupons" element={<ProtectedRoute adminOnly><AdminCoupons /></ProtectedRoute>} />
        <Route path="/admin/packages" element={<ProtectedRoute adminOnly><AdminPackages /></ProtectedRoute>} />
        <Route path="/admin/affiliates" element={<ProtectedRoute adminOnly><AdminAffiliates /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/revenue" element={<ProtectedRoute adminOnly><AdminRevenue /></ProtectedRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ChatWidget />
          <CouponWidget />
          <CookieConsent />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
